import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Plus, Pause, Play, AlertTriangle } from 'lucide-react';
import { updateDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { Checklist, ChecklistItem } from '@/firebase/checklistService';
import { checklistCompletionService } from '@/firebase/checklistCompletionService';
import { checklistRunService, ChecklistRun } from '@/firebase/checklistRunService';
import { getTimeDisplayStrings, calculateActiveTime, formatTimeDisplay } from '@/utils/checklistTimeTracker';
import { locationService, Location } from '@/firebase/locationService';
import { assetService } from '@/firebase/assetService';
import { userService } from '@/firebase/database';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { uploadEvidenceFile } from '@/firebase/taskCompletionService';
// PDF generation removed - no longer generating PDFs for checklist completions
import { complianceService } from '@/firebase/complianceService';
import { fetchUserRoles, getUserSystemRoleForOrganization, isSystemUserInOrganization } from '@/utils/roleHelpers';
import AddTaskPanel from './AddTaskPanel';
import { trackChecklist } from '@/utils/analytics';
import { extractFieldValuesFromDocument } from '@/services/geminiService';
import { Loader2, Upload } from 'lucide-react';
import { assetFactsService } from '@/firebase/assetFactsService';
import { getIndustryType } from '@/firebase/organizationService';
import { validateItemCompletion } from '@/services/checklistValidationService';
import { checklistEvidenceService } from '@/firebase/checklistEvidenceService';

interface RunChecklistProps {
  checklist: Checklist;
  assetId?: string; // Optional asset ID when running from asset page
  onClose: () => void;
  onComplete: () => void;
}

interface ItemPhoto {
  file?: File;
  preview?: string;
}

const sanitizeForFile = (value: string) => value.replace(/[^\w.\-()+@]+/g, '_');

const RunChecklist: React.FC<RunChecklistProps> = ({ checklist, assetId, onClose, onComplete }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const { hasFeature } = useSubscription();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [checklistStatus, setChecklistStatus] = useState<Record<string, boolean>>({});
  const [itemPhotos, setItemPhotos] = useState<Record<string, ItemPhoto>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddTaskPanel, setShowAddTaskPanel] = useState(false);
  const [selectedItemForTask, setSelectedItemForTask] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<{ text: string; index: number; item: ChecklistItem } | null>(null);
  const [itemEvidenceData, setItemEvidenceData] = useState<Record<string, {
    evidenceFiles: File[];
    fieldValues: Record<string, any>;
  }>>({});
  const [isDragActive, setIsDragActive] = useState(false);
  const [isAnalyzingFile, setIsAnalyzingFile] = useState<Record<string, boolean>>({}); // Track which files are being analyzed (key: itemText-evidenceIndex-fileIndex)
  const [ownerRole, setOwnerRole] = useState<string | undefined>(undefined);
  const [itemTaskReferences, setItemTaskReferences] = useState<Record<string, string[]>>({});
  // Run management state
  const [currentRun, setCurrentRun] = useState<ChecklistRun | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [timeDisplay, setTimeDisplay] = useState<{ activeTime: string; totalTime: string; pauseTime: string }>({
    activeTime: '0s',
    totalTime: '0s',
    pauseTime: '0s',
  });
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoadingRun, setIsLoadingRun] = useState(true);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  // Local start time tracking for checklists completed without pausing
  const localStartTimeRef = useRef<Date | null>(null);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);

  useEffect(() => {
    loadLocations();
    loadOwnerRole();
    // Don't initialize checklist yet - wait for run to load
  }, []);

  // Check if cancel should show confirmation
  const handleCancelClick = () => {
    // If checklist is already paused, allow cancel without warning (run is saved)
    if (isPaused && currentRun?.id) {
      onClose();
      return;
    }
    
    // Check if there are any unsaved changes (only if not paused)
    const hasChanges = Object.values(checklistStatus).some(v => v) || 
                      Object.values(itemPhotos).some(photo => photo.file || photo.preview) ||
                      Object.keys(variableValues).some(key => variableValues[key]?.trim());

    // Show confirmation if there are unsaved changes and checklist is not paused
    if (hasChanges) {
      setShowCancelConfirmModal(true);
    } else {
      // No changes, just close directly
      onClose();
    }
  };

  // Actually perform cancel after confirmation
  const handleConfirmCancel = async () => {
    setShowCancelConfirmModal(false);
    
    if (currentRun?.id && !isPaused) {
      // If run was created but not paused, delete it
      try {
        await checklistRunService.deleteRun(currentRun.id);
        console.log('Deleted uncommitted run on cancel');
      } catch (error) {
        console.error('Error deleting run on cancel:', error);
      }
    }
    stopTimeTracking();
    localStartTimeRef.current = null; // Reset local start time
    onClose();
  };

  // Only load paused runs when location is selected (for resume functionality)
  // Don't auto-create or auto-load in-progress runs
  useEffect(() => {
    if (user?.id && currentOrganization?.id && availableLocations.length > 0 && selectedLocation) {
      loadExistingPausedRun();
    } else {
      // No location selected yet - just initialize checklist
      if (availableLocations.length > 0) {
        initializeChecklist();
        setIsLoadingRun(false);
      }
    }
  }, [user?.id, currentOrganization?.id, checklist.id, availableLocations.length, selectedLocation?.id]);

  // If assetId is provided, fetch asset and prepopulate location
  useEffect(() => {
    const loadAssetLocation = async () => {
      if (assetId && !selectedLocation && availableLocations.length > 0) {
        try {
          const assetResult = await assetService.getAsset(assetId);
          if (assetResult.success && assetResult.data && assetResult.data.locationId) {
            // Check if the location is in the available locations list
            const assetLocation = availableLocations.find(
              loc => loc.id === assetResult.data.locationId
            );
            
            if (assetLocation) {
              // Location is accessible, set it
              setSelectedLocation(assetLocation);
            } else {
              // Location might not be accessible, but try to fetch it anyway
              // User might still be able to use it if they have access to the asset
              try {
                const locationResult = await locationService.getLocation(assetResult.data.locationId);
                if (locationResult.success && locationResult.data) {
                  setSelectedLocation(locationResult.data);
                }
              } catch (error) {
                console.error('Error loading asset location:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error loading asset location:', error);
        }
      }
    };
    
    loadAssetLocation();
  }, [assetId, availableLocations, selectedLocation]);


  const loadOwnerRole = async () => {
    if (checklist.templateId) {
      try {
        const templateResult = await complianceService.getTemplate(checklist.templateId);
        if (templateResult.success && templateResult.data) {
          setOwnerRole(templateResult.data.owner_role);
        }
      } catch (error) {
        console.warn('Error fetching owner role from template:', error);
      }
    }
  };

  // Load existing paused run only (for resume functionality)
  const loadExistingPausedRun = async () => {
    if (!user?.id || !checklist.id) {
      setIsLoadingRun(false);
      initializeChecklist(); // Still initialize checklist state
      return;
    }

    setIsLoadingRun(true);
    try {
      // Try to find existing active run for this checklist
      // Context must match both locationId AND assetId (if asset is provided)
      const context: { locationId?: string; assetId?: string } = {};
      if (selectedLocation?.id) context.locationId = selectedLocation.id;
      
      // Check for assetId from prop, or from variableValues (like "Room Number")
      let effectiveAssetId: string | undefined = assetId;
      if (!effectiveAssetId && variableValues) {
        // Check common variable names that might represent an asset/room
        effectiveAssetId = variableValues['Room Number'] || 
                          variableValues['Room'] || 
                          variableValues['assetId'] || 
                          variableValues['asset'];
      }
      if (effectiveAssetId) context.assetId = effectiveAssetId;

      // Only look for paused runs
      const pausedRunsResult = await checklistRunService.getActiveRunsForUser(user.id);
      
      if (pausedRunsResult.success && pausedRunsResult.data) {
        // Filter for this checklist and location+asset combination
        const matchingPausedRun = pausedRunsResult.data.find(run => {
          // Must match checklist
          if (run.checklistId !== checklist.id) return false;
          // Must be paused
          if (run.status !== 'paused') return false;
          // Must match location
          if (run.locationId !== context.locationId) return false;
          
          // Must match asset if provided
          if (context.assetId) {
            const runAssetId = run.assetId || 
                              run.variableValues?.['Room Number'] || 
                              run.variableValues?.['Room'] || 
                              run.variableValues?.['assetId'] || 
                              run.variableValues?.['asset'];
            if (runAssetId !== context.assetId) return false;
          } else {
            // No asset in context - run shouldn't have asset either
            if (run.assetId || run.variableValues?.['Room Number'] || run.variableValues?.['Room']) {
              return false;
            }
          }
          return true;
        });

        if (matchingPausedRun) {
          // Found a paused run - restore it
          setCurrentRun(matchingPausedRun);
          setIsPaused(true);

          // Restore state from run
          if (matchingPausedRun.items) setChecklistStatus(matchingPausedRun.items);
          if (matchingPausedRun.variableValues) setVariableValues(matchingPausedRun.variableValues);
          if (matchingPausedRun.itemTaskReferences) setItemTaskReferences(matchingPausedRun.itemTaskReferences);
          if (matchingPausedRun.photos) {
            const restoredPhotos: Record<string, ItemPhoto> = {};
            Object.entries(matchingPausedRun.photos).forEach(([key, url]) => {
              restoredPhotos[key] = { preview: url };
            });
            setItemPhotos(restoredPhotos);
          }
          
          // Set local start time from run's startedAt if available
          if (matchingPausedRun.startedAt) {
            const startedAtDate = matchingPausedRun.startedAt?.toDate 
              ? matchingPausedRun.startedAt.toDate() 
              : (matchingPausedRun.startedAt instanceof Date ? matchingPausedRun.startedAt : new Date());
            localStartTimeRef.current = startedAtDate;
          }
          
          updateTimeDisplay(matchingPausedRun);
        } else {
          // No paused run found - start fresh
          initializeChecklist();
        }
      } else {
        // No paused runs - start fresh
        initializeChecklist();
      }
    } catch (error) {
      console.error('Error loading/creating run:', error);
      // On error, just initialize checklist normally
      initializeChecklist();
    } finally {
      setIsLoadingRun(false);
      // loadHistory(); // TODO: Implement if needed
    }
  };

  // Create a new run - only called when user pauses
  const createNewRun = async (): Promise<boolean> => {
    if (!user?.id || !checklist.id || !selectedLocation) {
      return false;
    }

    try {
      // Fetch asset name if assetId is provided
      let assetName: string | undefined;
      let effectiveAssetId: string | undefined = assetId;
      
      // Also check if asset is specified in variableValues (like "Room Number")
      if (!effectiveAssetId && variableValues) {
        effectiveAssetId = variableValues['Room Number'] || 
                          variableValues['Room'] || 
                          variableValues['assetId'] || 
                          variableValues['asset'];
      }
      
      if (assetId) {
        const assetResult = await assetService.getAsset(assetId);
        if (assetResult.success && assetResult.data) {
          assetName = assetResult.data.assetName;
        }
      }

      // Upload photos before creating the run (if any)
      const photoUrls: Record<string, string> = {};
      for (const [itemText, photo] of Object.entries(itemPhotos)) {
        if (photo.file) {
          // Upload file to Firebase Storage
          try {
            const uploadResult = await uploadEvidenceFile(photo.file, {
              docType: 'checklist_evidence',
              organizationId: currentOrganization?.id,
              locationId: selectedLocation.id,
              checklistId: checklist.id,
              assetId,
              uploadedBy: user
                ? { id: user.id, email: user.email, name: user.name }
                : undefined,
              title: `${checklist.name || 'Checklist'} - ${itemText}`,
              evidenceLabel: itemText,
              source: 'web',
              additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
            });
            photoUrls[itemText] = uploadResult.url;
          } catch (error) {
            console.error(`Error uploading photo for ${itemText}:`, error);
            // Continue with other photos even if one fails
            // Use preview URL as fallback if available
            if (photo.preview) {
              photoUrls[itemText] = photo.preview;
            }
          }
        } else if (photo.preview) {
          // Photo already uploaded (from resumed run), use existing URL
          photoUrls[itemText] = photo.preview;
        }
      }

      const runResult = await checklistRunService.createRun({
        checklistId: checklist.id,
        checklistName: checklist.name,
        locationId: selectedLocation.id,
        locationName: selectedLocation.name,
        assetId: assetId,
        assetName: assetName,
        variableValues: variableValues, // This will contain room/asset info if specified as variable
        items: checklistStatus,
        photos: Object.keys(photoUrls).length > 0 ? photoUrls : undefined,
        itemTaskReferences: itemTaskReferences,
        startedBy: user.id,
        startedByName: user.name || user.email,
        organizationId: currentOrganization?.id,
        owner_role: ownerRole,
        source: 'web',
      });

      if (runResult.success && runResult.id) {
        // Track checklist started (when run is created)
        trackChecklist.started(checklist.id, assetId);
        // Don't start time tracking here - run will be paused immediately
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error creating run:', error);
      return false;
    }
  };

  // Start time tracking for active run
  const startTimeTracking = (run: ChecklistRun) => {
    // Update time display immediately
    updateTimeDisplay(run);

    // Set up interval to update time display every second
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
    }
    timeUpdateIntervalRef.current = setInterval(() => {
      if (currentRun) {
        updateTimeDisplay(currentRun);
      }
    }, 1000);
  };

  // Stop time tracking
  const stopTimeTracking = () => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  };

  // Update time display
  const updateTimeDisplay = (run: ChecklistRun) => {
    const timeStrings = getTimeDisplayStrings(run);
    setTimeDisplay({
      activeTime: timeStrings.activeTime,
      totalTime: timeStrings.totalTime,
      pauseTime: timeStrings.pauseTime,
    });
  };

  // Pause checklist
  const handlePause = async () => {
    if (!selectedLocation) {
      showNotification('Please select a location first', 'error');
      return;
    }

    setIsLoadingRun(true);
    
    try {
      let runToPause = currentRun;
      
      // If no run exists, create one first (this is when run is created)
      if (!runToPause?.id) {
        console.log('Creating run before pause...');
        const runCreated = await createNewRun();
        if (!runCreated) {
          showNotification('Failed to create checklist session', 'error');
          setIsLoadingRun(false);
          return;
        }
        
        // Wait a moment, then reload to get the created run
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Get the run we just created
        const context: { locationId?: string; assetId?: string } = {
          locationId: selectedLocation.id,
        };
        if (assetId) context.assetId = assetId;
        
        if (!user?.id) {
          showNotification('User not authenticated', 'error');
          setIsLoadingRun(false);
          return;
        }
        
        if (!checklist.id) {
          showNotification('Checklist ID missing', 'error');
          setIsLoadingRun(false);
          return;
        }
        
        const activeRunResult = await checklistRunService.getActiveRunForChecklist(
          checklist.id,
          user.id,
          context
        );
        
        if (activeRunResult.success && activeRunResult.data) {
          runToPause = activeRunResult.data;
          setCurrentRun(runToPause);
        } else {
          showNotification('Failed to create checklist session. Please try again.', 'error');
          setIsLoadingRun(false);
          return;
        }
      }

      if (!runToPause?.id) {
        showNotification('No run available to pause', 'error');
        setIsLoadingRun(false);
        return;
      }

      // Upload photos to Firebase Storage before pausing
      const photoUrls: Record<string, string> = {};
      for (const [itemText, photo] of Object.entries(itemPhotos)) {
        if (photo.file) {
          // Upload file to Firebase Storage
          try {
            const uploadResult = await uploadEvidenceFile(photo.file, {
              docType: 'checklist_evidence',
              organizationId: currentOrganization?.id,
              locationId: selectedLocation.id,
              checklistId: checklist.id,
              assetId,
              uploadedBy: user
                ? { id: user.id, email: user.email, name: user.name }
                : undefined,
              title: `${checklist.name || 'Checklist'} - ${itemText}`,
              evidenceLabel: itemText,
              source: 'web',
              additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
            });
            photoUrls[itemText] = uploadResult.url;
          } catch (error) {
            console.error(`Error uploading photo for ${itemText}:`, error);
            // Continue with other photos even if one fails
            // Use preview URL as fallback if available
            if (photo.preview) {
              photoUrls[itemText] = photo.preview;
            }
          }
        } else if (photo.preview) {
          // Photo already uploaded (from resumed run), use existing URL
          photoUrls[itemText] = photo.preview;
        }
      }

      // Save current progress before pausing (with uploaded photo URLs)
      await checklistRunService.updateRunProgress(runToPause.id, {
        items: checklistStatus,
        photos: photoUrls,
        itemTaskReferences: itemTaskReferences,
        variableValues: variableValues,
      });

      // Now pause the run
      const pauseResult = await checklistRunService.pauseRun(runToPause.id);
      if (pauseResult.success) {
        // Track checklist pause
        trackChecklist.paused(runToPause.id);
        
        // Reload run to get updated state
        const runData = await checklistRunService.getRun(runToPause.id);
        if (runData.success && runData.data) {
          setCurrentRun(runData.data);
          setIsPaused(true);
          stopTimeTracking();
          updateTimeDisplay(runData.data);
          showNotification('Checklist paused', 'info');
        }
      } else {
        showNotification(pauseResult.error || 'Failed to pause checklist', 'error');
      }
    } catch (error) {
      console.error('Error pausing checklist:', error);
      showNotification('Failed to pause checklist', 'error');
    } finally {
      setIsLoadingRun(false);
    }
  };

  // Resume checklist
  const handleResume = async () => {
    if (!currentRun?.id) {
      // If no run exists, create one
      await createNewRun();
      return;
    }

    try {
      const resumeResult = await checklistRunService.resumeRun(currentRun.id);
      if (resumeResult.success) {
        // Track checklist resume
        trackChecklist.resumed(currentRun.id);
        
        // Reload run to get updated state
        const runData = await checklistRunService.getRun(currentRun.id);
        if (runData.success && runData.data) {
          const updatedRun = runData.data;
          setCurrentRun(updatedRun);
          setIsPaused(false);
          
          // Restore state from run
          if (updatedRun.items) {
            setChecklistStatus(updatedRun.items);
          }
          if (updatedRun.variableValues) {
            setVariableValues(updatedRun.variableValues);
          }
          if (updatedRun.itemTaskReferences) {
            setItemTaskReferences(updatedRun.itemTaskReferences);
          }
          if (updatedRun.photos) {
            const restoredPhotos: Record<string, ItemPhoto> = {};
            Object.entries(updatedRun.photos).forEach(([key, url]) => {
              restoredPhotos[key] = { preview: url };
            });
            setItemPhotos(restoredPhotos);
          }
          
          startTimeTracking(updatedRun);
          showNotification('Checklist resumed', 'success');
        }
      } else {
        showNotification(resumeResult.error || 'Failed to resume checklist', 'error');
      }
    } catch (error) {
      console.error('Error resuming checklist:', error);
      showNotification('Failed to resume checklist', 'error');
    }
  };

  // Save progress to run (without changing status)
  // Only save if run exists - don't auto-create runs
  const saveProgress = async () => {
    if (!currentRun?.id) {
      // Don't auto-create runs - only create when user pauses
      return;
    }

    try {
      await checklistRunService.updateRunProgress(currentRun.id, {
        items: checklistStatus,
        photos: Object.fromEntries(
          Object.entries(itemPhotos)
            .filter(([_, photo]) => photo.preview)
            .map(([key, photo]) => [key, photo.preview || ''])
        ),
        itemTaskReferences: itemTaskReferences,
        variableValues: variableValues,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
      // Don't show error to user for auto-save failures
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      stopTimeTracking();
      localStartTimeRef.current = null; // Reset local start time on unmount
    };
  }, []);

  useEffect(() => {
    // Auto-select location if user has access to only one location
    if (availableLocations.length === 1 && !selectedLocation) {
      setSelectedLocation(availableLocations[0]);
    }
  }, [availableLocations.length, selectedLocation]);


  const loadLocations = async () => {
    // Fetch user's full data from Firestore to get locationIds
    let userLocationIdsArray: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIdsArray = userData.locationIds || [];
          // Use organization-specific role if available
          if (currentOrganization?.id) {
            userRole = getUserSystemRoleForOrganization(userData, currentOrganization.id) || userData.role;
            setIsSystemUser(isSystemUserInOrganization(userData, currentOrganization.id));
          } else {
            userRole = userData.role;
            // Fallback: check if user is a system user using legacy method
            const roles = await fetchUserRoles(user.id);
            setIsSystemUser(roles.isSystemUser);
          }
          setUserLocationIds(userLocationIdsArray || []);
        } else {
          setUserLocationIds([]);
          // Fallback: check if user is a system user using legacy method
          const roles = await fetchUserRoles(user.id);
          setIsSystemUser(roles.isSystemUser);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }

    // Load accessible locations (filtered by user's assigned locations)
    const result = await locationService.getAccessibleLocations(
      currentOrganization?.id,
      user?.id,
      userRole,
      userLocationIdsArray
    );
    if (result.success && result.data) {
      // Filter out any locations that might have a duplicate ID or are marked for deletion
      // Since Firestore delete actually removes the document, we should only see valid locations
      // However, to be safe, we'll filter by ensuring the location has a proper ID
      let validLocations = result.data.filter(loc => loc.id && loc.name);
      
      // If checklist has locationIds specified, filter to only show those locations
      if (checklist.locationIds && checklist.locationIds.length > 0) {
        validLocations = validLocations.filter(loc => 
          loc.id && checklist.locationIds!.includes(loc.id)
        );
        console.log(`[RunChecklist] Filtered locations by checklist.locationIds:`, {
          checklistLocationIds: checklist.locationIds,
          availableLocations: validLocations.map(l => ({ id: l.id, name: l.name }))
        });
      }
      
      setAvailableLocations(validLocations);
    }
  };

  const initializeChecklist = () => {
    const status: Record<string, boolean> = {};
    const photos: Record<string, ItemPhoto> = {};
    const vars: Record<string, string> = {};
    const taskRefs: Record<string, string[]> = {};

    checklist.items?.forEach(item => {
      status[item.text] = false;
      photos[item.text] = {};
      taskRefs[item.text] = [];
    });

    // Handle both legacy string[] and new ChecklistVariable[] format
    if (checklist.variables) {
      checklist.variables.forEach((variable) => {
        if (typeof variable === 'string') {
          // Legacy format
      vars[variable] = '';
        } else {
          // New format: ChecklistVariable
          vars[variable.name] = '';
        }
    });
    }

    // Track start time for time tracking (even if no run is created)
    if (!localStartTimeRef.current) {
      localStartTimeRef.current = new Date();
    }

    setChecklistStatus(status);
    setItemPhotos(photos);
    setVariableValues(vars);
    setItemTaskReferences(taskRefs);
  };

  const handleFileUpload = async (files: File[], evidenceDef: any, evidenceIndex: number) => {
    if (!selectedItem) return;

    // Get current file count to determine indices
    const currentFileCount = itemEvidenceData[selectedItem.text]?.evidenceFiles?.length || 0;

    // Add files to state first
    setItemEvidenceData(prev => ({
      ...prev,
      [selectedItem.text]: {
        ...prev[selectedItem.text],
        evidenceFiles: [
          ...(prev[selectedItem.text]?.evidenceFiles || []),
          ...files
        ]
      }
    }));

    // Analyze each file with AI for both field extraction and facts extraction
    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileIndex = currentFileCount + i;
        const fileKey = `${selectedItem.text}-${evidenceIndex}-${fileIndex}`;
        
        setIsAnalyzingFile(prev => ({ ...prev, [fileKey]: true }));

        try {
          // Extract field values if field definitions exist
          if (evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0) {
            const fieldResult = await extractFieldValuesFromDocument(file, evidenceDef.fieldDefinitions);
            
            if (fieldResult.fieldValues && Object.keys(fieldResult.fieldValues).length > 0) {
              // Merge extracted values with existing values (don't overwrite user-entered values)
              setItemEvidenceData(prev => {
                const existingValues = prev[selectedItem.text]?.fieldValues || {};
                const mergedValues: Record<string, any> = { ...existingValues };
                
                // Only add extracted values that are not null and don't already exist
                Object.entries(fieldResult.fieldValues).forEach(([key, value]) => {
                  if (value !== null && value !== undefined && !existingValues[key]) {
                    mergedValues[key] = value;
                  }
                });

                return {
                  ...prev,
                  [selectedItem.text]: {
                    ...prev[selectedItem.text],
                    fieldValues: mergedValues
                  }
                };
              });

              const extractedCount = Object.values(fieldResult.fieldValues).filter(v => v !== null && v !== undefined).length;
              if (extractedCount > 0) {
                showNotification(
                  `AI extracted ${extractedCount} field value${extractedCount !== 1 ? 's' : ''} from ${file.name}`,
                  'success'
                );
              }
            }
          }

          // Field extraction is already handled above using extractFieldValuesFromDocument
          // No need for separate bank-specific fact extraction - all fields come from checklist definitions
        } catch (error: any) {
          console.error('Error analyzing file:', error);
          showNotification(`Failed to analyze ${file.name}: ${error.message}`, 'error');
        } finally {
          setIsAnalyzingFile(prev => {
            const next = { ...prev };
            delete next[fileKey];
            return next;
          });
        }
      }
    }
  };

  const handleFileChange = (itemText: string, event: React.ChangeEvent<HTMLInputElement>) => {
    if (isPaused) {
      showNotification('Please resume the checklist to add photos', 'info');
      event.target.value = ''; // Clear file input
      return;
    }
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setItemPhotos(prev => ({
          ...prev,
          [itemText]: {
            file,
            preview: reader.result as string
          }
        }));
        // Don't auto-save - only save when user pauses
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (itemText: string) => {
    setItemPhotos(prev => ({
      ...prev,
      [itemText]: {}
    }));
  };

  const toggleChecklistItem = (itemText: string) => {
    if (isPaused) {
      showNotification('Please resume the checklist to make changes', 'info');
      return;
    }
    setChecklistStatus(prev => ({
      ...prev,
      [itemText]: !prev[itemText]
    }));
    // Don't auto-save - only save when user pauses
  };

  // PDF generation removed - readImageForPdf function no longer needed

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      showNotification('Please select a location', 'error');
      return;
    }

    // Check if checklist is complete - users cannot manually complete with missing items
    if (!isChecklistComplete) {
      showNotification('Please complete all checklist items before submitting', 'error');
      return;
    }

    // Don't create run here - runs are only created when user pauses
    // If completing without pausing, just complete directly (no run needed for time tracking)

    // Validate all variable values
    if (checklist.variables && checklist.variables.length > 0) {
      for (const variable of checklist.variables) {
        const varDef = typeof variable === 'string' 
          ? { name: variable as string, possibleValues: [] }
          : variable;
        
        if (!variableValues[varDef.name] || variableValues[varDef.name].trim() === '') {
          showNotification(`Please select a value for ${varDef.name}`, 'error');
          setIsSubmitting(false);
          return;
        }

        // If variable has predefined values, validate that the selected value is in the list
        if (varDef.possibleValues && varDef.possibleValues.length > 0) {
          if (!varDef.possibleValues.includes(variableValues[varDef.name])) {
            showNotification(`${varDef.name} must be one of the predefined values`, 'error');
            setIsSubmitting(false);
            return;
          }
        }
      }
    }
    
    setIsSubmitting(true);
    
    try {
      // Fetch owner_role from the checklist's compliance template BEFORE uploading files
      let ownerRole: string | undefined;
      if (checklist.templateId) {
        try {
          console.log('🔍 Fetching owner_role for checklist from template:', checklist.templateId);
          const templateResult = await complianceService.getTemplate(checklist.templateId);
          if (templateResult.success && templateResult.data) {
            ownerRole = templateResult.data.owner_role;
            console.log('✅ Found owner_role from template:', ownerRole);
          }
        } catch (error) {
          console.warn('⚠️ Error fetching compliance template for owner_role:', error);
          // Continue without owner_role if template fetch fails
        }
      } else {
        console.warn('⚠️ No templateId found for checklist:', checklist.id);
      }

      const photoUrls: Record<string, string> = {};
      
      // PDF generation removed - only upload photos, no PDF image processing
      for (const [itemText, photo] of Object.entries(itemPhotos)) {
        if (photo.file) {
          try {
            const uploadResult = await uploadEvidenceFile(photo.file, {
              docType: 'checklist_evidence',
              organizationId: currentOrganization?.id,
              locationId: selectedLocation.id,
              checklistId: checklist.id,
              assetId,
              uploadedBy: user
                ? { id: user.id, email: user.email, name: user.name }
                : undefined,
              title: `${checklist.name || 'Checklist'} - ${itemText}`,
              evidenceLabel: itemText,
              source: 'web',
              additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
            });

            photoUrls[itemText] = uploadResult.url;
          } catch (error) {
            console.error(`Error uploading photo for ${itemText}:`, error);
          }
        }
      }

      // Complete the run first if it exists
      let timeTrackingData: {
        startedAt?: any;
        activeTimeSeconds?: number;
        totalTimeSeconds?: number;
        pauseCount?: number;
        runId?: string;
      } = {};

      if (currentRun?.id) {
        // Complete the run and get time tracking data
        stopTimeTracking(); // Stop tracking before completing
        const completeResult = await checklistRunService.completeRun(currentRun.id, {
          completedBy: user?.id || '',
          completedByName: user ? `${user.name} (${user.email})` : 'Unknown',
        });

        if (completeResult.success && completeResult.run) {
          const run = completeResult.run;
          const totalTimeSeconds = Math.floor(
            ((run.completedAt?.toDate ? run.completedAt.toDate() : new Date()).getTime() -
             (run.startedAt?.toDate ? run.startedAt.toDate() : new Date()).getTime()) / 1000
          );

          timeTrackingData = {
            startedAt: run.startedAt,
            activeTimeSeconds: run.activeTimeSeconds,
            totalTimeSeconds: totalTimeSeconds,
            pauseCount: run.pauseDurations?.length || 0,
            runId: run.id,
          };
        }
      } else if (localStartTimeRef.current) {
        // No run was created (completed in one go) - calculate time from local start time
        const startTime = localStartTimeRef.current;
        const endTime = new Date();
        const activeTimeSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
        
        // Convert to Firestore Timestamp for consistency
        const startedAtValue = Timestamp.fromDate(startTime);

        timeTrackingData = {
          startedAt: startedAtValue,
          activeTimeSeconds: activeTimeSeconds,
          totalTimeSeconds: activeTimeSeconds, // Same as active time if no pauses
          pauseCount: 0,
        };
      }

      // Generate completion summary - check for missing fields and evidence
      let completionSummary: {
        totalItems: number;
        completedItems: number;
        incompleteItems: number;
        missingRequiredFields: Array<{ itemText: string; missingFields: string[] }>;
        missingRequiredEvidence: Array<{ itemText: string; evidenceCategory: string }>;
        needsReviewItems: string[];
      } | undefined = undefined;

      try {
        // Get asset facts for validation if assetId is provided
        let assetFacts: Record<string, any> = {};
        if (assetId) {
          const factsResult = await assetFactsService.getAssetFacts(assetId);
          if (factsResult.success && factsResult.data) {
            Object.entries(factsResult.data.facts || {}).forEach(([key, fact]) => {
              assetFacts[key] = fact;
            });
          }
        }

        // Build itemResults from checklistStatus
        const itemResults: Record<string, any> = {};
        checklist.items?.forEach(item => {
          const isCompleted = checklistStatus[item.text] === true;
          itemResults[item.text] = {
            status: isCompleted ? 'PASS' : 'NEEDS_REVIEW',
          };
        });

        // Validate each item and build summary
        const missingRequiredFields: Array<{ itemText: string; missingFields: string[] }> = [];
        const missingRequiredEvidence: Array<{ itemText: string; evidenceCategory: string }> = [];
        const needsReviewItems: string[] = [];
        let completedItems = 0;
        let incompleteItems = 0;

        for (const item of checklist.items || []) {
          const itemResult = itemResults[item.text];
          
          // Validate item (no longer need to enrich - fields come from evidenceDefinitions)
          const validation = validateItemCompletion(item, itemResult, assetFacts);
          
          if (validation.status === 'COMPLETE' && (itemResult?.status === 'PASS' || itemResult?.status === 'NA')) {
            completedItems++;
          } else {
            incompleteItems++;
          }

          // Check for missing required fields
          if (validation.missingFields.length > 0) {
            missingRequiredFields.push({
              itemText: item.text,
              missingFields: validation.missingFields,
            });
          }

          // Check for missing required evidence
          if (item.evidenceDefinitions) {
            for (const evidenceDef of item.evidenceDefinitions) {
              if (evidenceDef.required) {
                // Check if evidence exists for this item
                // Note: This is a simplified check - in production, you'd check actual evidence records
                // For now, we'll mark it as missing if the item is incomplete
                if (validation.status === 'INCOMPLETE') {
                  missingRequiredEvidence.push({
                    itemText: item.text,
                    evidenceCategory: evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence',
                  });
                }
              }
            }
          }

          // Check if item needs review
          if (validation.status === 'NEEDS_REVIEW' || itemResult?.status === 'NEEDS_REVIEW') {
            needsReviewItems.push(item.text);
          }
        }

        completionSummary = {
          totalItems: checklist.items?.length || 0,
          completedItems,
          incompleteItems,
          missingRequiredFields,
          missingRequiredEvidence,
          needsReviewItems,
        };
      } catch (error) {
        console.error('Error generating completion summary:', error);
        // Continue without summary if generation fails
      }

      const result = await checklistCompletionService.addCompletion({
        checklistId: checklist.id || '',
        checklistName: checklist.name,
        locationId: selectedLocation.id!,
        locationName: selectedLocation.name,
        assetId: assetId, // Include assetId if provided
        variableValues: variableValues,
        photos: Object.keys(photoUrls).length > 0 ? photoUrls : undefined,
        items: checklistStatus,
        completedBy: user?.id || '', // User ID
        completedByName: user ? `${user.name} (${user.email})` : 'Unknown', // Formatted display name
        organizationId: currentOrganization?.id,
        owner_role: ownerRole,
        source: 'web', // Mark as web app completion
        itemTaskReferences: Object.keys(itemTaskReferences).length > 0 ? itemTaskReferences : undefined,
        // Include time tracking data
        ...timeTrackingData,
        // Include completion summary
        completionSummary,
        // Set status based on summary
        status: completionSummary && (completionSummary.incompleteItems > 0 || completionSummary.missingRequiredFields.length > 0 || completionSummary.missingRequiredEvidence.length > 0)
          ? 'pending_review'
          : 'complete',
      });

      if (result.success && result.id) {
        // Track checklist completion
        trackChecklist.completed(checklist.id || '', assetId);
        
        let pdfError: Error | null = null;
        const completionId = result.id;
        const completionDate = new Date();
        
        // Fetch asset name if assetId is provided (check both prop and variableValues)
        let assetName: string | undefined;
        const assetIdToFetch = assetId || variableValues?.assetId;
        if (assetIdToFetch) {
          try {
            const assetResult = await assetService.getAsset(assetIdToFetch);
            if (assetResult.success && assetResult.data) {
              assetName = assetResult.data.assetName;
            }
          } catch (error) {
            console.error('Error fetching asset name for PDF:', error);
          }
        }
        
        // PDF Generation - DISABLED: PDF generation for checklist completions has been removed
        // PDF generation code has been removed as per requirements
        
        showNotification('Checklist completed successfully!', 'success');
        onComplete();
      } else if (result.success) {
        showNotification('Checklist completed successfully!', 'success');
        onComplete();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error) {
      console.error('Error completing checklist:', error);
      showNotification('Failed to complete checklist', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const completedCount = Object.values(checklistStatus).filter(v => v).length;
  const totalItems = checklist.items?.length || 0;
  const isChecklistComplete =
    totalItems === 0 ||
    (checklist.items ?? []).every(item => checklistStatus[item.text]);


  const formatDate = (date: any) => {
    try {
      if (date?.toDate) {
        return date.toDate().toLocaleString();
      } else if (date instanceof Date) {
        return date.toLocaleString();
      } else if (typeof date === 'number') {
        return new Date(date).toLocaleString();
      } else {
        return new Date(date).toLocaleString();
      }
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleTaskCreated = async (taskIds?: string[]) => {
    if (selectedItemForTask && taskIds && taskIds.length > 0) {
      // Mark the checklist item as completed
      setChecklistStatus(prev => ({
        ...prev,
        [selectedItemForTask]: true
      }));
      
      // Store task reference for this item
      setItemTaskReferences(prev => ({
        ...prev,
        [selectedItemForTask]: [...(prev[selectedItemForTask] || []), ...taskIds]
      }));
    }
    
    setShowAddTaskPanel(false);
    setSelectedItemForTask(null);
    // Don't call onComplete() - keep the checklist open so user can continue
  };

  return (
    <>
      {showAddTaskPanel && selectedLocation && selectedItemForTask && (
        <AddTaskPanel
          onClose={() => {
            setShowAddTaskPanel(false);
            setSelectedItemForTask(null);
          }}
          onTaskCreated={handleTaskCreated}
          prepopulatedLocationId={selectedLocation.id || undefined}
          prepopulatedOwnerRole={ownerRole}
          prepopulatedTaskName={`Fix: ${selectedItemForTask}`}
          isFromChecklist={true}
        />
      )}
      
      {/* Cancel Confirmation Modal */}
      {showCancelConfirmModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[130] flex items-center justify-center p-4"
          onClick={() => setShowCancelConfirmModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cancel Checklist?</h3>
                <p className="text-sm text-gray-600">Unsaved changes will be lost</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              Canceling will lose all unsaved changes. If you want to keep your progress, click <strong>"Pause"</strong> before canceling.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelConfirmModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors border border-gray-300"
              >
                Keep Working
              </button>
              <button
                onClick={handleConfirmCancel}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
              >
                Cancel Anyway
              </button>
            </div>
          </div>
        </div>
      )}
      
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-[95vw] w-full flex flex-col" style={{ maxHeight: '95vh', height: '95vh' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex-1">
            <h3 className="text-xl font-semibold">{checklist.name}</h3>
            {/* Time tracking and status display - only show if run exists and is paused */}
            {currentRun && isPaused && !isLoadingRun && (
              <div className="flex items-center gap-4 mt-1 text-sm">
                <span className="bg-white/20 px-2 py-0.5 rounded text-xs">
                  Paused
                </span>
                <span className="text-xs opacity-90">Active: {timeDisplay.activeTime}</span>
                {timeDisplay.pauseTime !== '0s' && (
                  <span className="text-xs opacity-90">Total: {timeDisplay.totalTime}</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCancelClick} className="text-white hover:bg-primary-700 rounded-full p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content: Form and Evidence Panel */}
        <div className="flex-1 overflow-hidden p-4" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full" style={{ minHeight: 0 }}>
            {/* Left: Form */}
            <form onSubmit={handleSubmit} className="flex flex-col h-full border border-gray-200 rounded-lg" style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
          {/* Location and Variable - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Location <span className="text-red-600">*</span>
              </label>
              <select
                value={selectedLocation?.id || ''}
                onChange={(e) => {
                  const location = availableLocations.find(l => l.id === e.target.value);
                  setSelectedLocation(location || null);
                }}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="">Select a location...</option>
                {availableLocations.map(location => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </div>

                  {/* Variable Input Fields */}
                  {checklist.variables && checklist.variables.length > 0 && (
                    <div className="space-y-3">
                      {checklist.variables.map((variable, idx) => {
                        const varDef = typeof variable === 'string' 
                          ? { name: variable as string, possibleValues: [] }
                          : variable;
                        const hasValues = varDef.possibleValues && varDef.possibleValues.length > 0;
                        
                        return (
                          <div key={idx}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                              {varDef.name} <span className="text-red-600">*</span>
                              {hasValues && (
                                <span className="text-xs text-gray-500 ml-1">
                                  ({varDef.possibleValues.length} option{varDef.possibleValues.length !== 1 ? 's' : ''})
                                </span>
                              )}
                </label>
                            {hasValues ? (
                              <select
                                value={variableValues[varDef.name] || ''}
                                onChange={(e) => {
                                  setVariableValues(prev => ({
                                    ...prev,
                                    [varDef.name]: e.target.value
                                  }));
                                }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                              >
                                <option value="">Select {varDef.name.toLowerCase()}...</option>
                                {varDef.possibleValues.map((value) => (
                                  <option key={value} value={value}>{value}</option>
                                ))}
                              </select>
                            ) : (
                <input
                  type="text"
                                value={variableValues[varDef.name] || ''}
                onChange={(e) => {
                    setVariableValues(prev => ({
                      ...prev,
                                    [varDef.name]: e.target.value
                    }));
                }}
                                placeholder={`Enter ${varDef.name.toLowerCase()}`}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
                            )}
                          </div>
                        );
                      })}
              </div>
            )}
          </div>

          {/* Checklist Items */}
          <div>
            <div className="flex items-center justify-between mb-2 pb-2 border-b">
              <h4 className="text-sm font-semibold text-gray-900">Checklist Items</h4>
              <span className="text-xs text-gray-500">
                {completedCount} / {totalItems} completed
              </span>
            </div>
            <div className="space-y-2">
              {checklist.items?.map((item, idx) => {
                const isCompleted = checklistStatus[item.text] || false;
                const taskRefs = itemTaskReferences[item.text] || [];
                return (
                <div 
                  key={idx} 
                  className={`bg-white border rounded-md p-2 cursor-pointer transition-colors ${
                    selectedItem?.text === item.text 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    console.log('[RunChecklist] Item clicked:', item.text, 'Has evidenceDefinitions:', !!item.evidenceDefinitions, 'Count:', item.evidenceDefinitions?.length || 0);
                    setSelectedItem({ text: item.text, index: idx, item });
                  }}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleChecklistItem(item.text);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                    />
                    <div className="flex-1 flex items-start justify-between gap-2">
                    <div className="flex-1">
                        <label className={`text-xs font-medium cursor-pointer block ${isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {idx + 1}. {item.text}
                      </label>
                        {isCompleted && taskRefs.length > 0 && (
                          <div className="mt-1 text-xs text-blue-600">
                            Task{taskRefs.length > 1 ? 's' : ''} created: {taskRefs.length} reference{taskRefs.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                      {!isCompleted && selectedLocation && hasFeature('create_custom_tasks') && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedItemForTask(item.text);
                            setShowAddTaskPanel(true);
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-300 rounded hover:bg-blue-100 transition-colors"
                          title="Create task for this failed item"
                        >
                          <Plus className="w-3 h-3" />
                          Create Task
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Photo Upload Section - Compact */}
                  <div className="ml-6">
                    {itemPhotos[item.text]?.preview ? (
                      <div className="relative inline-block">
                        <img
                          src={itemPhotos[item.text].preview}
                          alt="Upload preview"
                          className="w-20 h-20 object-cover rounded border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(item.text)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-300 rounded cursor-pointer hover:bg-gray-100 w-fit">
                        <Camera className="w-3 h-3 text-gray-600" />
                        <span className="text-xs text-gray-700">Photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handleFileChange(item.text, e)}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>
                );
              })}
                  </div>
            </div>
          </div>

          {/* Footer */}
              <div className="flex justify-end gap-2 p-3 border-t border-gray-200 bg-white flex-shrink-0">
            <button
              type="button"
              onClick={handleCancelClick}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            {/* Pause/Resume button - show when location is selected */}
            {!isLoadingRun && !isSubmitting && selectedLocation && (
              <>
                {currentRun && isPaused ? (
                  <button
                    type="button"
                    onClick={handleResume}
                    className="px-3 py-1.5 text-sm border border-blue-300 rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center gap-1.5 transition-colors"
                    title="Resume checklist"
                  >
                    <Play className="w-4 h-4" />
                    Resume
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handlePause}
                    className="px-3 py-1.5 text-sm border border-orange-300 rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 flex items-center gap-1.5 transition-colors"
                    title="Pause checklist"
                  >
                    <Pause className="w-4 h-4" />
                    Pause
                  </button>
                )}
              </>
            )}
            <button
              type="submit"
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isSubmitting || !isChecklistComplete}
              title={!isChecklistComplete ? 'Complete all checklist items to submit' : undefined}
            >
              {isSubmitting ? 'Completing...' : 'Complete'}
            </button>
          </div>
        </form>

            {/* Right: Evidence & Fields Panel */}
            {selectedItem ? (
              <div className="border border-gray-200 rounded-lg flex flex-col h-full" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b rounded-t-lg flex-shrink-0">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Evidence & Fields</h4>
                    <p className="text-xs text-gray-600 mt-0.5">{selectedItem.index + 1}. {selectedItem.text}</p>
                  </div>
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="text-gray-500 hover:text-gray-700 p-1"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-4" style={{ minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  {(() => {
                    // Get the item from checklist to ensure we have the latest data with evidenceDefinitions
                    const checklistItem = checklist.items?.find(i => i.text === selectedItem.text);
                    const itemToUse = checklistItem || selectedItem.item;
                    console.log('[RunChecklist] Displaying evidence for item:', selectedItem.text, 'Has evidenceDefinitions:', !!itemToUse?.evidenceDefinitions, 'Count:', itemToUse?.evidenceDefinitions?.length || 0);
                    
                    return itemToUse?.evidenceDefinitions && itemToUse.evidenceDefinitions.length > 0 ? (
                      itemToUse.evidenceDefinitions.map((evidenceDef, evIdx) => (
                      <div key={evIdx} className="bg-white border border-gray-200 rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-xs font-semibold text-gray-900">{evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence'}</h5>
                          {evidenceDef.required && (
                            <span className="text-xs text-red-600">Required</span>
                          )}
                        </div>
                        {evidenceDef.description && (
                          <p className="text-xs text-gray-600 mb-3">{evidenceDef.description}</p>
                        )}
                        
                        {/* File Upload with Drag and Drop */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Upload Evidence File
                          </label>
                          <div
                            onDrop={(e) => {
                              e.preventDefault();
                              setIsDragActive(false);
                              const files = Array.from(e.dataTransfer.files);
                              handleFileUpload(files, evidenceDef, evIdx);
                            }}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDragActive(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              setIsDragActive(false);
                            }}
                            className={`
                              border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer
                              ${isDragActive 
                                ? 'border-primary-500 bg-primary-50' 
                                : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                              }
                            `}
                          >
                            <input
                              type="file"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                handleFileUpload(files, evidenceDef, evIdx);
                                // Reset input so same file can be selected again
                                e.target.value = '';
                              }}
                              className="hidden"
                              id={`file-input-${selectedItem.text}-${evIdx}`}
                            />
                            <label htmlFor={`file-input-${selectedItem.text}-${evIdx}`} className="cursor-pointer">
                              <Upload className={`w-5 h-5 mx-auto mb-2 ${isDragActive ? 'text-primary-600' : 'text-gray-400'}`} />
                              <p className="text-xs text-gray-600">
                                {isDragActive ? 'Drop files here' : 'Drag and drop files here or click to browse'}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">PDF, Word, or Images</p>
                            </label>
                          </div>
                          {itemEvidenceData[selectedItem.text]?.evidenceFiles && itemEvidenceData[selectedItem.text].evidenceFiles.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {itemEvidenceData[selectedItem.text].evidenceFiles.map((file, fileIdx) => {
                                const fileKey = `${selectedItem.text}-${evIdx}-${fileIdx}`;
                                const isAnalyzing = isAnalyzingFile[fileKey] || false;
                                return (
                                  <div key={fileIdx} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="text-gray-700 truncate">{file.name}</span>
                                      {isAnalyzing && (
                                        <Loader2 className="w-3 h-3 animate-spin text-primary-600 flex-shrink-0" />
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setItemEvidenceData(prev => ({
                                          ...prev,
                                          [selectedItem.text]: {
                                            ...prev[selectedItem.text],
                                            evidenceFiles: prev[selectedItem.text]?.evidenceFiles.filter((_, idx) => idx !== fileIdx) || []
                                          }
                                        }));
                                      }}
                                      className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                                      disabled={isAnalyzing}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Field Definitions */}
                        {evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0 && (
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Fields</label>
                            {evidenceDef.fieldDefinitions.map((fieldDef, fieldIdx) => (
                              <div key={`${evIdx}-${fieldDef.key}-${fieldIdx}`} className="space-y-1">
                                <label className="block text-xs text-gray-700">
                                  {fieldDef.label}
                                  {fieldDef.required && <span className="text-red-600 ml-1">*</span>}
                                </label>
                                {fieldDef.type === 'date' ? (
                                  <input
                                    type="date"
                                    value={itemEvidenceData[selectedItem.text]?.fieldValues?.[fieldDef.key] || ''}
                                    onChange={(e) => {
                                      setItemEvidenceData(prev => ({
                                        ...prev,
                                        [selectedItem.text]: {
                                          ...prev[selectedItem.text],
                                          fieldValues: {
                                            ...(prev[selectedItem.text]?.fieldValues || {}),
                                            [fieldDef.key]: e.target.value
                                          }
                                        }
                                      }));
                                    }}
                                    className="w-full text-xs border border-gray-300 rounded p-2"
                                    required={fieldDef.required}
                                  />
                                ) : fieldDef.type === 'boolean' ? (
                                  <select
                                    value={itemEvidenceData[selectedItem.text]?.fieldValues?.[fieldDef.key] || ''}
                                    onChange={(e) => {
                                      setItemEvidenceData(prev => ({
                                        ...prev,
                                        [selectedItem.text]: {
                                          ...prev[selectedItem.text],
                                          fieldValues: {
                                            ...(prev[selectedItem.text]?.fieldValues || {}),
                                            [fieldDef.key]: e.target.value === 'true'
                                          }
                                        }
                                      }));
                                    }}
                                    className="w-full text-xs border border-gray-300 rounded p-2"
                                    required={fieldDef.required}
                                  >
                                    <option value="">Select...</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                  </select>
                                ) : fieldDef.type === 'enum' && fieldDef.enumValues ? (
                                  <select
                                    value={itemEvidenceData[selectedItem.text]?.fieldValues?.[fieldDef.key] || ''}
                                    onChange={(e) => {
                                      setItemEvidenceData(prev => ({
                                        ...prev,
                                        [selectedItem.text]: {
                                          ...prev[selectedItem.text],
                                          fieldValues: {
                                            ...(prev[selectedItem.text]?.fieldValues || {}),
                                            [fieldDef.key]: e.target.value
                                          }
                                        }
                                      }));
                                    }}
                                    className="w-full text-xs border border-gray-300 rounded p-2"
                                    required={fieldDef.required}
                                  >
                                    <option value="">Select...</option>
                                    {fieldDef.enumValues.map(val => (
                                      <option key={val} value={val}>{val}</option>
                                    ))}
                                  </select>
                                ) : fieldDef.type === 'list' ? (
                                  <textarea
                                    value={(() => {
                                      const v = itemEvidenceData[selectedItem.text]?.fieldValues?.[fieldDef.key];
                                      return Array.isArray(v) ? v.join('\n') : (v != null ? String(v) : '');
                                    })()}
                                    onChange={(e) => {
                                      const arr = e.target.value ? e.target.value.split(/\n/).map(s => s.trim()).filter(Boolean) : [];
                                      setItemEvidenceData(prev => ({
                                        ...prev,
                                        [selectedItem.text]: {
                                          ...prev[selectedItem.text],
                                          fieldValues: {
                                            ...(prev[selectedItem.text]?.fieldValues || {}),
                                            [fieldDef.key]: arr
                                          }
                                        }
                                      }));
                                    }}
                                    rows={3}
                                    className="w-full text-xs border border-gray-300 rounded p-2"
                                    placeholder="One item per line"
                                  />
                                ) : (
                                  <input
                                    type={fieldDef.type === 'number' ? 'number' : 'text'}
                                    value={itemEvidenceData[selectedItem.text]?.fieldValues?.[fieldDef.key] ?? ''}
                                    onChange={(e) => {
                                      setItemEvidenceData(prev => ({
                                        ...prev,
                                        [selectedItem.text]: {
                                          ...prev[selectedItem.text],
                                          fieldValues: {
                                            ...(prev[selectedItem.text]?.fieldValues || {}),
                                            [fieldDef.key]: fieldDef.type === 'number' ? Number(e.target.value) : e.target.value
                                          }
                                        }
                                      }));
                                    }}
                                    className="w-full text-xs border border-gray-300 rounded p-2"
                                    required={fieldDef.required}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : null;
                  })()}
                  
                  {(() => {
                    // Get the item from checklist to ensure we have the latest data with evidenceDefinitions
                    const checklistItem = checklist.items?.find(i => i.text === selectedItem.text);
                    const itemToUse = checklistItem || selectedItem.item;
                    
                    return !itemToUse?.evidenceDefinitions || itemToUse.evidenceDefinitions.length === 0 ? (
                      <div className="text-center py-8 text-xs text-gray-500">
                        No evidence definitions for this item.
                        <br />
                        Click on an item with evidence requirements to see upload and field options.
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg flex flex-col h-full items-center justify-center" style={{ minHeight: 0 }}>
                <div className="text-center px-4 py-8">
                  <p className="text-sm text-gray-500 mb-2">Select a checklist item</p>
                  <p className="text-xs text-gray-400">Click on any item to view and manage evidence requirements and fields</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default RunChecklist;

